import React, { useCallback, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePublicLinkInBio } from "../../../hooks/usePublicLinkInBio";
import { getSupabasePublicClient } from "../../../lib/supabasePublicClient";
import { buildProfileFromRpcData } from "../../../lib/linkInBio/buildProfileFromRpcData";
import LinkInBioPublicView from "../../linkInBio/LinkInBioPublicView";
import { trackLinkInBioClick } from "../../../lib/linkInBio/trackLinkInBioClick";
import { LinkInBioPublicPageSkeleton } from "./LinkInBioSkeletons";

const LinkInBioPublicPage = ({
  finitlessProject: finitlessProjectProp,
  slug: slugProp,
} = {}) => {
  const params = useParams();
  const { t } = useTranslation("linkInBioPublic");
  const supabaseClient = useMemo(() => getSupabasePublicClient(), []);

  const finitlessProject = String(
    finitlessProjectProp ?? params.project ?? "",
  ).trim();
  const slug = slugProp ?? params.slug;
  const normalizedSlug = slug ? String(slug).trim().toLowerCase() : "";
  const { data, isLoading, isError, error, refetch, isFetching } =
    usePublicLinkInBio(slug, finitlessProject);

  useEffect(() => {
    let meta = document.querySelector(
      'meta[name="robots"][data-link-in-bio="1"]',
    );
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "robots";
      meta.setAttribute("data-link-in-bio", "1");
      document.head.appendChild(meta);
    }
    meta.content = "noindex,nofollow";
    return () => {
      meta?.remove();
    };
  }, []);

  useEffect(() => {
    if (data?.display_name) {
      document.title = t("pageTitle", { name: data.display_name });
    } else {
      document.title = t("pageTitleFallback");
    }
    return () => {
      document.title = t("pageTitleFallback");
    };
  }, [data?.display_name, t]);

  const { profile, primaryResolved, primaryLabel } = useMemo(
    () => buildProfileFromRpcData(data, t),
    [data, t],
  );

  const handleTrackedButtonClick = useCallback(
    (buttonKey) => {
      const projectFromRow =
        data?.finitless_project != null &&
        String(data.finitless_project).trim() !== ""
          ? String(data.finitless_project).trim()
          : "";
      trackLinkInBioClick({
        slug: normalizedSlug,
        buttonKey,
        project: projectFromRow || finitlessProject || undefined,
      });
    },
    [data?.finitless_project, finitlessProject, normalizedSlug],
  );

  if (!supabaseClient) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#fafafa] px-6 py-16 text-center">
        <h1 className="text-lg font-semibold text-slate-900">
          {t("misconfigured.title")}
        </h1>
        <p className="mt-2 max-w-md text-sm text-slate-600">
          {t("misconfigured.body")}
        </p>
      </div>
    );
  }

  if (!normalizedSlug || !finitlessProject) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#fafafa] px-6 py-16 text-center">
        <h1 className="text-lg font-semibold text-slate-900">
          {t("unavailable.title")}
        </h1>
        <p className="mt-2 max-w-md text-sm text-slate-600">
          {t("unavailable.body")}
        </p>
      </div>
    );
  }

  if (isLoading || (isFetching && !data)) {
    return <LinkInBioPublicPageSkeleton />;
  }

  if (isError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#fafafa] px-6 py-16 text-center">
        <h1 className="text-lg font-semibold text-slate-900">
          {t("error.title")}
        </h1>
        <p className="mt-2 max-w-md text-sm text-slate-600">
          {error?.message || t("error.generic")}
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-6 min-h-11 rounded-xl bg-[#8a42ff] px-6 py-2.5 text-sm font-semibold text-white shadow-sm ring-offset-2 transition hover:opacity-95 focus-visible:ring-2 focus-visible:ring-[#8a42ff] focus-visible:outline-none"
        >
          {t("error.retry")}
        </button>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#fafafa] px-6 py-16 text-center">
        <h1 className="text-lg font-semibold text-slate-900">
          {t("unavailable.title")}
        </h1>
        <p className="mt-2 max-w-md text-sm text-slate-600">
          {t("unavailable.body")}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafa] px-5 pt-10 pb-16 sm:px-6">
      <LinkInBioPublicView
        profile={profile}
        primaryResolved={primaryResolved}
        primaryLabel={primaryLabel}
        poweredByLabel={t("poweredBy")}
        interactive
        onTrackedButtonClick={handleTrackedButtonClick}
      />
    </div>
  );
};

export default LinkInBioPublicPage;
